import React, { FC, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import {
  FeedbackScale,
  FeedbackTextareaField,
  GradientWrapper,
  Notification,
  ThankYouCard,
} from "@app/components";
import { usePatientActions } from "@app/provider";
import { HEAP_EVENTS } from "@app/provider/types";
import { Button } from "@natera/form";

import "./feedback.scss";

const messages = defineMessages({
  feedbackPageTitle: {
    id: "feedbackPageTitle",
    defaultMessage: "Patient Portal Feedback",
  },
  feedbackPageSubtitle: {
    id: "feedbackPageSubtitle",
    defaultMessage:
      "Thank you for taking the time to share your feedback on Natera’s Patient Portal.",
  },
  feedbackPageQuestion: {
    id: "feedbackPageQuestion",
    defaultMessage:
      "How would you rate your experience using Natera’s Patient Portal today?",
  },
  feedbackScaleVeryBad: {
    id: "feedbackScaleVeryBad",
    defaultMessage: "Very bad",
  },
  feedbackScaleVeryGood: {
    id: "feedbackScaleVeryGood",
    defaultMessage: "Very good",
  },
  feedbackSubmitButton: {
    id: "feedbackSubmitButton",
    defaultMessage: "Submit Feedback",
  },
  feedbackScoreReasonLabel: {
    id: "feedbackScoreReasonLabel",
    defaultMessage: "In a few words, why did you choose this score?",
  },
  feedbackScoreReasonPlaceholder: {
    id: "feedbackScoreReasonPlaceholder",
    defaultMessage: "Tell us about your experience",
  },
  feedbackAdditionalFeedbackLabel: {
    id: "feedbackAdditionalFeedbackLabel",
    defaultMessage:
      "Is there any other feedback you’d like to share about Natera’s Patient Portal?",
  },
  feedbackAdditionalFeedbackPlaceholder: {
    id: "feedbackAdditionalFeedbackPlaceholder",
    defaultMessage: "Any additional comments or suggestions",
  },
  feedbackSubmitError: {
    id: "feedbackSubmitError",
    defaultMessage:
      "We're sorry, we couldn't save your feedback right now. Please try again later.",
  },
  feedbackThankYouTitle: {
    id: "feedbackThankYouTitle",
    defaultMessage: "Thank you!",
  },
  feedbackThankYouMessage: {
    id: "feedbackThankYouMessage",
    defaultMessage: "We really appreciate your feedback.",
  },
});

const MAX_FEEDBACK_CHARS = 1000;

const Feedback: FC = () => {
  const intl = useIntl();
  const { submitPatientFeedback } = usePatientActions();
  const [rating, setRating] = useState<number | null>(null);
  const [scoreReason, setScoreReason] = useState("");
  const [additionalFeedback, setAdditionalFeedback] = useState("");
  const [submitError, setSubmitError] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (rating == null) {
      return;
    }

    heap.track(HEAP_EVENTS.upp_core_share_feedback_submit);

    const trimmedReason = scoreReason.trim();
    const trimmedGeneral = additionalFeedback.trim();

    setSubmitError(false);
    setIsSubmitting(true);
    try {
      await submitPatientFeedback({
        score: rating,
        ...(trimmedReason ? { scoreReason: trimmedReason } : {}),
        ...(trimmedGeneral ? { generalFeedback: trimmedGeneral } : {}),
      });
      setSubmitSuccess(true);
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <GradientWrapper display>
      {submitSuccess ? (
        <div className="patient-portal-feedback ">
          <ThankYouCard
            title={intl.formatMessage(messages.feedbackThankYouTitle)}
            message={intl.formatMessage(messages.feedbackThankYouMessage)}
          />
        </div>
      ) : (
        <div className="patient-portal-feedback">
          {submitError ? (
            <Notification
              type="error"
              role="alert"
              className="patient-portal-feedback__error"
            >
              {intl.formatMessage(messages.feedbackSubmitError)}
            </Notification>
          ) : null}
          <div className="patient-portal-feedback__container">
            <div className="patient-portal-feedback__content">
              <h1 className="patient-portal-feedback__title">
                {intl.formatMessage(messages.feedbackPageTitle)}
              </h1>
              <p className="patient-portal-feedback__subtitle">
                {intl.formatMessage(messages.feedbackPageSubtitle)}
              </p>
              <p className="patient-portal-feedback__question">
                {intl.formatMessage(messages.feedbackPageQuestion)}
              </p>
              <FeedbackScale
                className="patient-portal-feedback__scale"
                value={rating}
                onChange={setRating}
                leftLabel={intl.formatMessage(messages.feedbackScaleVeryBad)}
                rightLabel={intl.formatMessage(messages.feedbackScaleVeryGood)}
              />
              <FeedbackTextareaField
                id="patient-portal-feedback-score-reason"
                label={intl.formatMessage(messages.feedbackScoreReasonLabel)}
                value={scoreReason}
                onChange={setScoreReason}
                placeholder={intl.formatMessage(
                  messages.feedbackScoreReasonPlaceholder
                )}
                maxLength={MAX_FEEDBACK_CHARS}
                rows={3}
              />
              <FeedbackTextareaField
                id="patient-portal-feedback-additional"
                label={intl.formatMessage(
                  messages.feedbackAdditionalFeedbackLabel
                )}
                value={additionalFeedback}
                onChange={setAdditionalFeedback}
                placeholder={intl.formatMessage(
                  messages.feedbackAdditionalFeedbackPlaceholder
                )}
                maxLength={MAX_FEEDBACK_CHARS}
                rows={4}
              />
              <Button
                type="button"
                unelevated
                loading={isSubmitting}
                disabled={rating === null || isSubmitting}
                onClick={handleSubmit}
              >
                {intl.formatMessage(messages.feedbackSubmitButton)}
              </Button>
            </div>
          </div>
        </div>
      )}
    </GradientWrapper>
  );
};

export default Feedback;
